/**
 * 개인정보 처리방침 — 개인정보 보호법 제30조가 요구하는 공개 페이지.
 *
 * 설계 근거는 `docs/10_설계/개인정보-보유기간-결정.md` §6-3이고, 게시 주체·보호책임자·문의처는
 * 이티랩 회신(2026-08-06 [1-3])으로 확정된 값이다. **여기 적는 내용은 사이트가 실제로 하는
 * 처리와 일치해야 한다** — 기능이 바뀌면(연락처 수집 배선·수집 항목 추가) 이 페이지와
 * 시행일을 함께 고치고, 동의 문구 버전(`consent_log.policy_version`)을 올린다.
 *
 * 보유기간·파기 규칙의 정본은 위 결정 문서다. 여기서는 시민에게 하는 약속으로 다시 쓴 것이므로
 * 문서와 어긋나게 고치지 않는다.
 */

const SECTIONS = [
  {
    title: '1. 무엇을, 왜 받나요',
    body: (
      <>
        <table className="admin-table">
          <thead>
            <tr>
              <th>언제</th>
              <th>받는 것</th>
              <th>왜</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>문제를 등록하실 때</td>
              <td>문제 내용(자유 서술) · 대상 · 동네</td>
              <td>해결할 수 있는 기업·단체를 찾기 위해</td>
            </tr>
            <tr>
              <td>연결을 원하실 때</td>
              <td>이름 · 연락처</td>
              <td>운영기관이 직접 연락드리기 위해 (화면에 별도 동의를 받습니다)</td>
            </tr>
            <tr>
              <td>기업·단체 등록 시</td>
              <td>담당자 이름 · 연락처 · 이메일</td>
              <td>연결 운영 목적. 공개 화면에는 절대 나가지 않습니다</td>
            </tr>
            <tr>
              <td>사업자등록번호 확인 시</td>
              <td>대표자 성명 · 개업일자</td>
              <td>국세청 진위확인에만 쓰고 <b>저장하지 않습니다</b></td>
            </tr>
          </tbody>
        </table>
        <p className="hint">
          문제 내용에 건강 등 민감한 정보가 들어갈 수 있어, 등록 화면에서 그 처리에 대한 별도
          동의를 받습니다. 이름·연락처·병명 등 개인을 알아볼 수 있는 정보는 문제 내용에 적지
          말아 주세요.
        </p>
      </>
    ),
  },
  {
    title: '2. 언제까지 갖고 있나요',
    body: (
      <>
        <p>
          <b>수집일로부터 1년</b>입니다. 그 전이라도 다음 중 하나가 먼저 오면 지체 없이
          파기합니다.
        </p>
        <ul>
          <li>동의를 철회하시거나 삭제를 요청하신 때</li>
          <li>연결이 끝나고 후속 확인까지 마친 때(목적 달성)</li>
          <li>플랫폼 운영이 끝날 때(전량 파기)</li>
        </ul>
        <p className="hint">
          파기는 매일 자동으로 확인해 처리하며, 개인을 알아볼 수 있는 값을 지운 뒤의 통계적
          기록(예: 김○○, 문제 내용)만 남습니다.
        </p>
      </>
    ),
  },
  {
    title: '3. 누구에게 넘기나요',
    body: (
      <>
        <p>
          제3자에게 제공하지 않습니다. 연결이 성사되어도 연락처를 기업에 넘기지 않고{' '}
          <b>운영기관이 직접 연락</b>드립니다.
        </p>
        <p>
          시스템 개발·운영은 <b>주식회사 코드넛</b>에 위탁하고 있으며, 위탁 계약으로 개인정보
          보호 의무를 지웁니다.
        </p>
      </>
    ),
  },
  {
    title: '4. 여러분의 권리',
    body: (
      <>
        <p>
          언제든 본인의 개인정보에 대해 <b>열람·정정·삭제·처리정지·동의 철회</b>를 요청하실 수
          있습니다. 아래 문의처로 연락 주시면 지체 없이 처리합니다.
        </p>
        <p>
          <b>만 14세 미만</b>은 보호자 동의를 받은 뒤 등록해 주세요. 보호자 동의가 확인되지
          않는 경우 해당 정보는 삭제 조치합니다.
        </p>
      </>
    ),
  },
  {
    title: '5. 책임자와 문의처',
    body: (
      <table className="admin-table">
        <tbody>
          <tr>
            <th>처리방침 게시 주체</th>
            <td>주식회사 이티랩</td>
          </tr>
          <tr>
            <th>개인정보 보호책임자</th>
            <td>양준영 이사</td>
          </tr>
          <tr>
            <th>문의처</th>
            <td>
              <a href="mailto:etlab.official@gmail.com">etlab.official@gmail.com</a>
            </td>
          </tr>
        </tbody>
      </table>
    ),
  },
]

export function PrivacyPage() {
  return (
    <div>
      <h1 className="page-title">개인정보 처리방침</h1>
      <p className="page-desc">
        해봄은 광명 지역의 문제와 해결 주체를 잇기 위해 꼭 필요한 최소한의 개인정보만
        다룹니다. 이 방침은 개인정보 보호법 제30조에 따라 공개합니다.
      </p>

      {SECTIONS.map((s) => (
        <section key={s.title} className="panel" style={{ marginBottom: 16 }}>
          <h2 className="section-title" style={{ marginTop: 0 }}>
            {s.title}
          </h2>
          {s.body}
        </section>
      ))}

      <p className="muted small">시행일: 2026-08-06 (v1)</p>
    </div>
  )
}
